import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Clock, ArrowRight, User } from 'lucide-react';

const Blog = () => {
  const posts = [
    {
      slug: 'why-twe-exists',
      title: 'Why Twe Exists',
      excerpt: 'Technology was meant to empower people. Somewhere along the way, it became complicated, fragmented, and exclusionary. Twe exists to fix that.',
      date: 'January 15, 2025',
      readTime: '8 min read',
      author: 'Tu Dang (Tusdang)',
      category: 'Mission',
      image: '/logo.png'
    },
    {
      slug: 'about-the-journey',
      title: 'About the Journey',
      excerpt: 'Every product begins with an idea. Twe began with a problem that refused to be ignored. The story of how constraints shaped creation.',
      date: 'January 10, 2025',
      readTime: '6 min read',
      author: 'Tu Dang (Tusdang)',
      category: 'Story',
      image: '/logo.png'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <BookOpen className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Twe Blog
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Stories, insights, and the philosophy behind building technology that empowers everyone
          </p>
        </div>

        {/* Featured Post */}
        <div className="mb-16">
          <Link
            to={`/blog/${posts[0].slug}`}
            className="group block bg-gradient-to-br from-zinc-900 to-zinc-950 border border-orange-500/30 rounded-2xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-[1.02]"
          >
            <div className="grid md:grid-cols-2 gap-8 p-8">
              <div className="flex items-center justify-center bg-black/40 rounded-xl p-8">
                <img src={posts[0].image} alt={posts[0].title} className="w-32 h-32 object-contain" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-500 text-xs font-semibold rounded-full border border-orange-500/30 w-fit mb-4">
                  Featured • {posts[0].category}
                </span>
                <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-orange-500 transition-colors">
                  {posts[0].title}
                </h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {posts[0].excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>{posts[0].author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{posts[0].date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>{posts[0].readTime}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-6 h-6 text-orange-500 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* All Posts */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-8">All Posts</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {posts.map((post, idx) => (
              <Link
                key={idx}
                to={`/blog/${post.slug}`}
                className="group bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="p-6">
                  <div className="flex items-center justify-center bg-black/40 rounded-lg p-6 mb-6">
                    <img src={post.image} alt={post.title} className="w-20 h-20 object-contain" />
                  </div>
                  <span className="inline-block px-3 py-1 bg-orange-500/10 text-orange-500 text-xs font-semibold rounded-full border border-orange-500/20 mb-4">
                    {post.category}
                  </span>
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-500 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Newsletter CTA */}
        <div className="mt-16 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Stay Updated
          </h3>
          <p className="text-gray-300 mb-6">
            Get notified when we publish new articles about voice automation, productivity, and accessibility
          </p>
          <a
            href="mailto:contact@twe-assistant.com?subject=Subscribe to Twe Blog"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300"
          >
            <span>Subscribe via Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Blog;